<template>
  <div class="dashboard-body">
    <div class="row">
      <sidenav class="custom-col-width-2" />
      <div class="custom-col-width-10">
        <navbar />
        <div class="content-section">
          <!-- Hero -->
          <div class="dashboard-hero">
            <div>
              <div class="dashboard-eyebrow">
                <span class="status-dot"></span>
                Verification analytics
              </div>
              <h1>Welcome back{{ account ? `, ${$filters.name(account.fname)}` : '' }}</h1>
              <p>
                A live overview of your transaction verifications, bank activity and subscription across Abol Verify.
              </p>
            </div>
            <div class="hero-controls">
              <button type="button" class="refresh-btn" :disabled="isLoading" v-on:click="refresh">
                <i :class="['pi', isLoading ? 'pi-spin pi-spinner' : 'pi-refresh']"></i>
                <span>Refresh</span>
              </button>
            </div>
          </div>

          <!-- Loading (first load) -->
          <div v-if="isLoading && !hasLoaded" class="dashboard-loading">
            <i class="pi pi-spin pi-spinner"></i>
            <span>Loading your dashboard…</span>
          </div>

          <template v-else>
            <!-- Subscription status banner (hidden when on an unlimited plan) -->
            <article v-if="showSubscriptionBanner" class="sub-banner" :class="subscriptionTone.bannerCls">
              <div class="sub-banner-main">
                <span class="sub-banner-label">
                  <i class="pi pi-crown"></i>
                  Subscription
                </span>
                <div class="sub-banner-row">
                  <h2 class="sub-banner-plan">{{ subPlan ? subPlan.name : 'No active plan' }}</h2>
                  <span class="badge" :class="subscriptionTone.badgeCls">{{ subscriptionTone.label }}</span>
                </div>
                <p class="sub-banner-meta">
                  <template v-if="currentSubscription">
                    <i class="pi pi-calendar-clock"></i>
                    {{ subPlan && subPlan.isFree ? 'Trial ends' : 'Renews / expires' }}
                    {{ $filters.getDateOnly(currentSubscription.endDate) }}
                    <span class="dot-sep">•</span>
                    {{ daysRemainingLabel }}
                  </template>
                  <template v-else>
                    Choose a plan to keep verifying transactions without limits.
                  </template>
                </p>
              </div>

              <div class="sub-banner-usage" v-if="currentSubscription">
                <div class="usage-top">
                  <span class="usage-key">{{ usage.label }}</span>
                  <span class="usage-val">{{ usage.text }}</span>
                </div>
                <div v-if="usage.hasLimit" class="usage-bar">
                  <span class="usage-fill" :class="{ 'is-high': usage.percent >= 90 }" :style="{ width: usage.percent + '%' }"></span>
                </div>
                <div v-else class="usage-unlimited">
                  <i class="pi pi-infinity"></i>
                  Unlimited verifications
                </div>
              </div>
            </article>

            <!-- Analytics always render — with empty (zeroed) data before the
                 first verification, so the charts are visible from day one. -->
            <!-- KPI cards -->
            <div class="stats-grid">
                <article class="stat-card">
                  <div class="stat-card-top">
                    <span class="stat-icon indigo"><i class="pi pi-shield"></i></span>
                    <span class="stat-chip">{{ fmt(thisMonthCount) }} this month</span>
                  </div>
                  <h2>{{ fmt(total) }}</h2>
                  <p>Total Verifications</p>
                  <span class="stat-sub">{{ fmt(todayCount) }} today</span>
                </article>

                <article class="stat-card">
                  <div class="stat-card-top">
                    <span class="stat-icon green"><i class="pi pi-verified"></i></span>
                    <span class="stat-chip">{{ successRate }}% success</span>
                  </div>
                  <h2>{{ fmt(statusCounts.verified) }}</h2>
                  <p>Verified</p>
                  <span class="stat-sub">{{ fmt(statusCounts.rejected) }} rejected · {{ fmt(statusCounts.failed) }} failed</span>
                </article>

                <article class="stat-card">
                  <div class="stat-card-top">
                    <span class="stat-icon amber"><i class="pi pi-clock"></i></span>
                    <span v-if="statusCounts.pending" class="stat-chip">needs review</span>
                  </div>
                  <h2>{{ fmt(statusCounts.pending) }}</h2>
                  <p>Pending</p>
                  <span class="stat-sub">awaiting a decision</span>
                </article>

                <article class="stat-card">
                  <div class="stat-card-top">
                    <span class="stat-icon teal"><i class="pi pi-wallet"></i></span>
                    <span class="stat-chip">verified</span>
                  </div>
                  <h2 class="amount">{{ money(amountVerified) }}</h2>
                  <p>Amount Verified</p>
                  <span class="stat-sub">{{ money(amountVerifiedThisMonth) }} this month</span>
                </article>
              </div>

              <!-- Monthly activity line -->
              <article class="dashboard-card">
                <div class="card-header">
                  <div>
                    <h3>This Month's Activity</h3>
                    <p>Daily verifications for {{ currentMonthLabel }} — total vs. verified.</p>
                  </div>
                  <div class="card-legend">
                    <span class="lg"><i class="dot" style="background: var(--ehs-color-primary)"></i> Total</span>
                    <span class="lg"><i class="dot" style="background: var(--ehs-color-success)"></i> Verified</span>
                  </div>
                </div>
                <apexchart type="line" height="320" :options="monthlyChart.options" :series="monthlyChart.series" />
              </article>

              <!-- Distributions -->
              <div class="chart-grid chart-grid-2">
                <article class="dashboard-card">
                  <div class="card-header">
                    <div>
                      <h3>Verification Outcomes</h3>
                      <p>Successful vs. failed across all transactions.</p>
                    </div>
                  </div>
                  <apexchart type="donut" height="300" :options="outcomeChart.options" :series="outcomeChart.series" />
                </article>

                <article class="dashboard-card">
                  <div class="card-header">
                    <div>
                      <h3>Verifications by Bank</h3>
                      <p>Volume per bank, broken down by outcome.</p>
                    </div>
                  </div>
                  <apexchart type="bar" height="300" :options="bankChart.options" :series="bankChart.series" />
                </article>
              </div>

              <!-- Recent + highlights -->
              <div class="lower-grid">
                <article class="dashboard-card recent-card">
                  <div class="card-header">
                    <div>
                      <h3>Recent Verifications</h3>
                      <p>Your latest transaction checks.</p>
                    </div>
                  </div>
                  <ul class="recent-list">
                    <li v-if="!recentVerifications.length" class="recent-empty">
                      <i class="pi pi-inbox"></i>
                      <span>No verifications yet — they'll show up here.</span>
                    </li>
                    <li v-for="v in recentVerifications" :key="v.id" class="recent-item">
                      <span class="recent-bank">{{ bankLabel(v.bank) }}</span>
                      <div class="recent-info">
                        <span class="recent-amount">{{ formatAmount(v) }}</span>
                        <span class="recent-ref">{{ v.transactionReference || v.transactionId || v.accountName || '—' }}</span>
                      </div>
                      <div class="recent-meta">
                        <span class="badge" :class="statusBadgeClass(v.status)">{{ statusLabel(v.status) }}</span>
                        <span class="recent-date">{{ $filters.getDateOnly(v.date || v.createdAt) }}</span>
                      </div>
                    </li>
                  </ul>
                </article>

                <article class="dashboard-card highlights-card">
                  <div class="card-header">
                    <div>
                      <h3>Highlights</h3>
                      <p>Key numbers at a glance.</p>
                    </div>
                  </div>
                  <div class="highlight-rows">
                    <div class="highlight-row">
                      <span class="hl-icon green"><i class="pi pi-chart-line"></i></span>
                      <div class="hl-text">
                        <span class="hl-label">Success rate</span>
                        <span class="hl-value">{{ successRate }}%</span>
                      </div>
                    </div>
                    <div class="highlight-row">
                      <span class="hl-icon teal"><i class="pi pi-calculator"></i></span>
                      <div class="hl-text">
                        <span class="hl-label">Avg. verified amount</span>
                        <span class="hl-value">{{ money(avgVerifiedAmount) }}</span>
                      </div>
                    </div>
                    <div class="highlight-row">
                      <span class="hl-icon indigo"><i class="pi pi-building-columns"></i></span>
                      <div class="hl-text">
                        <span class="hl-label">Busiest bank</span>
                        <span class="hl-value">{{ busiestBank.label }} <small v-if="busiestBank.total">· {{ fmt(busiestBank.total) }}</small></span>
                      </div>
                    </div>
                    <div class="highlight-row">
                      <span class="hl-icon blue"><i class="pi pi-wallet"></i></span>
                      <div class="hl-text">
                        <span class="hl-label">Bank accounts</span>
                        <span class="hl-value">{{ fmt(activeBankAccounts) }} active <small v-if="totalBankAccounts">/ {{ fmt(totalBankAccounts) }}</small></span>
                      </div>
                    </div>
                  </div>
                </article>
              </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from "vue3-apexcharts";
import sidenav from "@/components/Sidenav.vue";
import navbar from "@/components/Navbar.vue";
import Verification from "@/services/verification";
import Subscription from "@/services/subscription";
import BankAccount from "@/services/recipient_account";
import { BANK_LABELS, BANKS, VERIFICATION_STATUS } from "@/utils/enums";
import { DEFAULT_CURRENCY } from "@/utils/const";

const COLORS = {
  primary: "#73c41d",
  verified: "#4aa12a",
  pending: "#f5a623",
  rejected: "#e0464d",
  failed: "#e0464d",
};

const STATUS_BADGE = {
  [VERIFICATION_STATUS.verified]: "badge-success",
  [VERIFICATION_STATUS.pending]: "badge-warning",
  [VERIFICATION_STATUS.rejected]: "badge-danger",
  [VERIFICATION_STATUS.failed]: "badge-danger",
};

const AXIS_STYLE = { colors: "#6b7088", fontSize: "11px", fontWeight: 600 };
const GRID = { borderColor: "#e5e7ee", strokeDashArray: 4 };

export default {
  title: "Dashboard",
  components: {
    sidenav,
    navbar,
    apexchart: VueApexCharts,
  },
  data() {
    return {
      verifications: [],
      total: 0,
      subscriptions: [],
      bankAccounts: [],
      isLoading: false,
      hasLoaded: false,
    };
  },
  computed: {
    account() {
      return this.$store.getters.account.data;
    },
    // ---- Verification aggregates ------------------------------------------
    statusCounts() {
      const counts = { verified: 0, pending: 0, rejected: 0, failed: 0 };
      for (const v of this.verifications) {
        if (counts[v.status] != null) counts[v.status] += 1;
      }
      return counts;
    },
    successRate() {
      if (!this.total) return 0;
      return Math.round((this.statusCounts.verified / this.total) * 100);
    },
    thisMonthCount() {
      return this.verifications.filter((v) => this.isThisMonth(v.date || v.createdAt)).length;
    },
    todayCount() {
      return this.verifications.filter((v) => this.isToday(v.date || v.createdAt)).length;
    },
    primaryCurrency() {
      const freq = {};
      for (const v of this.verifications) {
        const c = v.currency || DEFAULT_CURRENCY;
        freq[c] = (freq[c] || 0) + 1;
      }
      let best = DEFAULT_CURRENCY;
      let max = -1;
      for (const c of Object.keys(freq)) {
        if (freq[c] > max) { max = freq[c]; best = c; }
      }
      return best;
    },
    amountVerified() {
      return this.verifications
        .filter((v) => v.status === VERIFICATION_STATUS.verified)
        .reduce((sum, v) => sum + (Number(v.amount) || 0), 0);
    },
    amountVerifiedThisMonth() {
      return this.verifications
        .filter((v) => v.status === VERIFICATION_STATUS.verified && this.isThisMonth(v.date || v.createdAt))
        .reduce((sum, v) => sum + (Number(v.amount) || 0), 0);
    },
    avgVerifiedAmount() {
      const n = this.statusCounts.verified;
      return n ? this.amountVerified / n : 0;
    },
    bankStats() {
      // Seed with the known banks so they always appear in a stable order.
      const order = [BANKS.cbe, BANKS.telebirr, BANKS.boa];
      const map = {};
      const ensure = (bank) => {
        if (!map[bank]) {
          map[bank] = { bank, label: this.bankLabel(bank), total: 0, verified: 0, pending: 0, rejected: 0, failed: 0 };
        }
        return map[bank];
      };
      order.forEach(ensure);
      for (const v of this.verifications) {
        const row = ensure(v.bank || "unknown");
        row.total += 1;
        if (row[v.status] != null) row[v.status] += 1;
      }
      return Object.values(map).sort((a, b) => b.total - a.total);
    },
    busiestBank() {
      const withData = this.bankStats.filter((b) => b.total > 0);
      return withData.length ? withData[0] : { label: "—", total: 0 };
    },
    recentVerifications() {
      return [...this.verifications]
        .sort((a, b) => new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt))
        .slice(0, 6);
    },
    activeBankAccounts() {
      return this.bankAccounts.filter((b) => b.isActive).length;
    },
    totalBankAccounts() {
      return this.bankAccounts.length;
    },
    currentMonthLabel() {
      return new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" });
    },
    // ---- Subscription ------------------------------------------------------
    currentSubscription() {
      return this.subscriptions.find((s) => s.isActive && !s.isPending) || null;
    },
    subPlan() {
      return this.currentSubscription ? this.currentSubscription.subscriptionPlan : null;
    },
    // "Unlimited" = an active plan with no verification cap. Such users have
    // nothing to upgrade toward, so we hide the subscription banner for them.
    isUnlimitedPlan() {
      return !!(this.currentSubscription && this.subPlan && this.subPlan.verificationLimit == null);
    },
    showSubscriptionBanner() {
      return !this.isUnlimitedPlan;
    },
    subscriptionTone() {
      const sub = this.currentSubscription;
      if (!sub) return { label: "Inactive", badgeCls: "badge-secondary", bannerCls: "is-none" };
      const ended = sub.endDate && new Date(sub.endDate).getTime() < Date.now();
      if (ended) return { label: "Expired", badgeCls: "badge-danger", bannerCls: "is-warn" };
      return { label: "Active", badgeCls: "badge-success", bannerCls: "is-active" };
    },
    daysRemainingLabel() {
      const sub = this.currentSubscription;
      if (!sub || !sub.endDate) return "—";
      const diff = new Date(sub.endDate).getTime() - Date.now();
      if (diff <= 0) return "Expired";
      const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
      return days === 1 ? "1 day left" : `${days} days left`;
    },
    usage() {
      const plan = this.subPlan;
      if (!plan || plan.verificationLimit == null) {
        return { hasLimit: false, label: "Verifications", text: "Unlimited", percent: 0 };
      }
      const total = (() => {
        if (plan.limitPeriod === "total") {
          return { used: this.total, label: "Verifications used (lifetime)" };
        }
        return { used: this.thisMonthCount, label: "Verifications used this month" };
      })();
      const limit = Number(plan.verificationLimit);
      const percent = limit > 0 ? Math.min(100, Math.round((total.used / limit) * 100)) : 0;
      return {
        hasLimit: true,
        label: total.label,
        text: `${this.fmt(total.used)} / ${this.fmt(limit)}`,
        percent,
      };
    },
    // ---- Charts ------------------------------------------------------------
    monthlyChart() {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const totalByDay = new Array(daysInMonth).fill(0);
      const verifiedByDay = new Array(daysInMonth).fill(0);

      for (const v of this.verifications) {
        const raw = v.date || v.createdAt;
        if (!raw) continue;
        const dt = new Date(raw);
        if (isNaN(dt.getTime()) || dt.getFullYear() !== year || dt.getMonth() !== month) continue;
        const idx = dt.getDate() - 1;
        totalByDay[idx] += 1;
        if (v.status === VERIFICATION_STATUS.verified) verifiedByDay[idx] += 1;
      }

      const categories = Array.from({ length: daysInMonth }, (_, i) => String(i + 1));

      return {
        series: [
          { name: "Total", type: "area", data: totalByDay },
          { name: "Verified", type: "line", data: verifiedByDay },
        ],
        options: {
          chart: { fontFamily: "inherit", toolbar: { show: false }, zoom: { enabled: false } },
          colors: [COLORS.primary, COLORS.verified],
          stroke: { width: [2, 2.5], curve: "smooth" },
          fill: {
            type: ["gradient", "solid"],
            gradient: { shadeIntensity: 1, opacityFrom: 0.32, opacityTo: 0.04, stops: [0, 95] },
          },
          dataLabels: { enabled: false },
          grid: { ...GRID, padding: { left: 6, right: 6 } },
          markers: { size: 0, hover: { size: 5 } },
          xaxis: {
            categories,
            axisBorder: { color: "#d1d5dc" },
            axisTicks: { show: false },
            tickAmount: Math.min(15, daysInMonth),
            labels: { style: AXIS_STYLE },
            title: { text: `Day of ${this.currentMonthLabel}`, style: { color: "#9aa0b4", fontSize: "10px", fontWeight: 600 } },
          },
          yaxis: {
            labels: { style: AXIS_STYLE, formatter: (v) => Math.round(v) },
            min: 0,
          },
          legend: { show: false },
          tooltip: { shared: true, intersect: false, x: { formatter: (val) => `${this.currentMonthLabel.split(" ")[0]} ${val}` } },
          noData: { text: "No activity this month", style: { color: "#9aa0b4" } },
        },
      };
    },
    outcomeChart() {
      const s = this.statusCounts;
      return {
        series: [s.verified, s.pending, s.rejected, s.failed],
        options: this.donutOptions(
          ["Verified", "Pending", "Rejected", "Failed"],
          [COLORS.verified, COLORS.pending, COLORS.rejected, COLORS.failed],
          "verification",
        ),
      };
    },
    bankChart() {
      const rows = this.bankStats;
      return {
        series: [
          { name: "Verified", data: rows.map((r) => r.verified) },
          { name: "Pending", data: rows.map((r) => r.pending) },
          { name: "Rejected", data: rows.map((r) => r.rejected) },
          { name: "Failed", data: rows.map((r) => r.failed) },
        ],
        options: {
          chart: { type: "bar", stacked: true, fontFamily: "inherit", toolbar: { show: false } },
          colors: [COLORS.verified, COLORS.pending, COLORS.rejected, COLORS.failed],
          plotOptions: { bar: { columnWidth: "45%", borderRadius: 4, borderRadiusApplication: "end" } },
          dataLabels: { enabled: false },
          grid: { ...GRID, padding: { left: 6, right: 6 } },
          xaxis: {
            categories: rows.map((r) => r.label),
            axisBorder: { color: "#d1d5dc" },
            axisTicks: { show: false },
            labels: { style: AXIS_STYLE },
          },
          yaxis: { labels: { style: AXIS_STYLE, formatter: (v) => Math.round(v) }, min: 0 },
          legend: { position: "top", horizontalAlign: "right", fontSize: "12px", markers: { radius: 4 } },
          tooltip: { shared: true, intersect: false },
          noData: { text: "No bank data", style: { color: "#9aa0b4" } },
        },
      };
    },
  },
  methods: {
    fmt(n) {
      return Number(n || 0).toLocaleString();
    },
    money(value) {
      return this.$filters.formatPrice(Number(value || 0), this.primaryCurrency);
    },
    bankLabel(bank) {
      return BANK_LABELS[bank] || String(bank || "").toUpperCase();
    },
    statusLabel(status) {
      return status ? status.charAt(0).toUpperCase() + status.slice(1) : "—";
    },
    statusBadgeClass(status) {
      return STATUS_BADGE[status] || "badge-secondary";
    },
    formatAmount(v) {
      const amount = Number(v.amount);
      if (isNaN(amount)) return "—";
      return this.$filters.formatPrice(amount, v.currency || null);
    },
    isThisMonth(raw) {
      if (!raw) return false;
      const dt = new Date(raw);
      const now = new Date();
      return !isNaN(dt.getTime()) && dt.getFullYear() === now.getFullYear() && dt.getMonth() === now.getMonth();
    },
    isToday(raw) {
      if (!raw) return false;
      const dt = new Date(raw);
      const now = new Date();
      return (
        !isNaN(dt.getTime()) &&
        dt.getFullYear() === now.getFullYear() &&
        dt.getMonth() === now.getMonth() &&
        dt.getDate() === now.getDate()
      );
    },
    donutOptions(labels, colors, unitLabel = "item") {
      return {
        chart: { fontFamily: "inherit", toolbar: { show: false } },
        labels,
        colors,
        stroke: { width: 2, colors: ["#ffffff"] },
        legend: {
          position: "bottom",
          fontSize: "12px",
          fontWeight: 500,
          labels: { colors: "#3f4259" },
          markers: { radius: 4 },
          itemMargin: { horizontal: 8, vertical: 3 },
        },
        dataLabels: {
          enabled: true,
          formatter: (val) => `${Math.round(val)}%`,
          style: { fontSize: "11px", fontWeight: 600 },
          dropShadow: { enabled: false },
        },
        plotOptions: {
          pie: {
            donut: {
              size: "66%",
              labels: {
                show: true,
                value: { fontSize: "1.5rem", fontWeight: 700, color: "#34404a" },
                total: {
                  show: true,
                  label: "Total",
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  color: "#6b7088",
                  formatter: (w) => w.globals.seriesTotals.reduce((a, b) => a + b, 0).toLocaleString(),
                },
              },
            },
          },
        },
        states: { hover: { filter: { type: "lighten", value: 0.06 } } },
        tooltip: { y: { formatter: (v) => `${v} ${unitLabel}${v === 1 ? "" : "s"}` } },
        noData: { text: "No data", style: { color: "#9aa0b4" } },
      };
    },
    async loadAll() {
      this.isLoading = true;
      try {
        const [verRes, subRes, bankRes] = await Promise.all([
          Verification.getVerifications({ page: 1, limit: 100000 }),
          Subscription.getSubscriptions({}).catch(() => null),
          BankAccount.getBankAccounts({ page: 1, limit: 100000 }).catch(() => null),
        ]);

        if (verRes?.data?.status) {
          this.verifications = verRes.data.data || [];
          this.total = verRes.data.metaData?.totalRecords ?? this.verifications.length;
        }
        if (subRes?.data?.status) this.subscriptions = subRes.data.data || [];
        if (bankRes?.data?.status) this.bankAccounts = bankRes.data.data || [];
      } finally {
        this.isLoading = false;
        this.hasLoaded = true;
      }
    },
    refresh() {
      this.loadAll();
    },
  },
  mounted() {
    this.loadAll();
  },
};
</script>

<style scoped>
.dashboard-hero {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 24px;
  margin-bottom: 18px;
}

.dashboard-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  width: fit-content;
  padding: 6px 12px;
  margin-bottom: 10px;
  border: 1px solid var(--ehs-color-border);
  border-radius: var(--ehs-radius-pill);
  color: var(--ehs-color-primary-active);
  background: var(--ehs-color-primary-soft);
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--ehs-color-success);
  box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.12);
}

.dashboard-hero h1 {
  margin: 0;
  color: var(--ehs-color-ink);
  font-family: var(--ehs-font-display);
  font-size: 1.7rem;
  font-weight: 600;
  letter-spacing: -0.03em;
}

.dashboard-hero p {
  max-width: 640px;
  margin: 8px 0 0;
  color: var(--ehs-color-muted);
  font-size: 0.9rem;
  line-height: 1.55;
}

.hero-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.refresh-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 15px;
  border: 1px solid var(--ehs-color-border);
  background: var(--ehs-color-surface);
  border-radius: var(--ehs-radius-pill);
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--ehs-color-ink);
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.refresh-btn:hover:not(:disabled) {
  background: var(--ehs-color-primary-soft);
  border-color: var(--ehs-color-primary-soft);
  color: var(--ehs-color-primary-hover);
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.dashboard-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 80px 24px;
  background: var(--ehs-color-surface);
  border: 1px solid var(--ehs-color-border);
  border-radius: var(--ehs-radius-lg);
  color: var(--ehs-color-muted);
  text-align: center;
}

.dashboard-loading .pi {
  font-size: 1.8rem;
  color: var(--ehs-color-soft);
}

/* Subscription banner */
.sub-banner {
  display: flex;
  align-items: center;
  gap: 22px;
  padding: 18px 22px;
  margin-bottom: 18px;
  border: 1px solid var(--ehs-color-border);
  border-radius: var(--ehs-radius-xl);
  background:
    radial-gradient(120% 160% at 0% 0%, rgba(115, 196, 29, 0.10) 0%, rgba(115, 196, 29, 0) 55%),
    var(--ehs-color-surface);
  box-shadow: var(--ehs-shadow-card);
}

.sub-banner.is-warn {
  background:
    radial-gradient(120% 160% at 0% 0%, rgba(245, 166, 35, 0.12) 0%, rgba(245, 166, 35, 0) 55%),
    var(--ehs-color-surface);
}

.sub-banner.is-none {
  background: var(--ehs-color-canvas);
}

.sub-banner-main {
  flex: 1 1 auto;
  min-width: 0;
}

.sub-banner-label {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--ehs-color-muted);
}

.sub-banner-label .pi {
  color: var(--ehs-color-primary);
  font-size: 0.8rem;
}

.sub-banner-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 4px;
}

.sub-banner-plan {
  margin: 0;
  font-family: var(--ehs-font-display);
  font-size: 1.35rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--ehs-color-ink);
  text-transform: capitalize;
}

.sub-banner-meta {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 6px 0 0;
  font-size: 0.82rem;
  color: var(--ehs-color-muted);
}

.sub-banner-meta .pi {
  font-size: 0.8rem;
}

.dot-sep {
  color: var(--ehs-color-soft);
}

.sub-banner-usage {
  flex: 0 0 230px;
  max-width: 230px;
}

.usage-top {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 7px;
}

.usage-key {
  font-size: 0.72rem;
  color: var(--ehs-color-muted);
  font-weight: 500;
}

.usage-val {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--ehs-color-ink);
  white-space: nowrap;
}

.usage-bar {
  position: relative;
  height: 8px;
  border-radius: var(--ehs-radius-pill);
  background: var(--ehs-color-canvas-strong);
  overflow: hidden;
}

.usage-fill {
  position: absolute;
  inset: 0 auto 0 0;
  border-radius: var(--ehs-radius-pill);
  background: var(--ehs-color-primary);
  transition: width 0.4s ease;
}

.usage-fill.is-high {
  background: var(--ehs-color-danger);
}

.usage-unlimited {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--ehs-color-primary-active);
}

.sub-banner-cta {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  flex-shrink: 0;
  padding: 9px 16px;
  border: 1px solid var(--ehs-color-primary);
  border-radius: var(--ehs-radius-md);
  background: var(--ehs-color-primary);
  color: #ffffff;
  font-size: 0.82rem;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.sub-banner-cta:hover {
  background: var(--ehs-color-primary-hover);
  border-color: var(--ehs-color-primary-hover);
}

.sub-banner-cta .pi {
  font-size: 0.72rem;
}

/* KPI cards */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}

.stat-card {
  position: relative;
  overflow: hidden;
  padding: 16px;
  border: 1px solid var(--ehs-color-border);
  background: var(--ehs-color-surface);
  border-radius: var(--ehs-radius-lg);
  box-shadow: var(--ehs-shadow-card);
}

.stat-card::before {
  content: "";
  position: absolute;
  inset: 0 0 auto 0;
  height: 3px;
  background: var(--ehs-color-primary);
}

.stat-card-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 36px;
}

.stat-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 12px;
  font-size: 0.95rem;
  background: var(--ehs-color-primary-soft);
  color: var(--ehs-color-primary);
}

.stat-icon.indigo { background: var(--ehs-color-primary-soft); color: var(--ehs-color-primary); }
.stat-icon.blue { background: #ecfeff; color: #0ea5e9; }
.stat-icon.green { background: #f0fdf4; color: var(--ehs-color-success); }
.stat-icon.amber { background: #fffbeb; color: var(--ehs-color-warning); }
.stat-icon.teal { background: #f0fdfa; color: #0d9488; }

.stat-chip {
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--ehs-color-muted);
  background: var(--ehs-color-canvas);
  border-radius: var(--ehs-radius-pill);
  padding: 3px 9px;
}

.stat-card h2 {
  margin: 14px 0 2px;
  color: var(--ehs-color-ink);
  font-family: var(--ehs-font-display);
  font-size: 1.55rem;
  font-weight: 600;
  letter-spacing: -0.025em;
}

.stat-card h2.amount {
  font-size: 1.3rem;
}

.stat-card p {
  margin: 0;
  color: var(--ehs-color-muted);
  font-size: 0.78rem;
  font-weight: 600;
}

.stat-sub {
  display: block;
  margin-top: 6px;
  font-size: 0.72rem;
  color: var(--ehs-color-soft);
}

/* Chart cards */
.chart-grid {
  display: grid;
  gap: 14px;
  margin-bottom: 14px;
}

.chart-grid-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }

.dashboard-card {
  border: 1px solid var(--ehs-color-border);
  background: var(--ehs-color-surface);
  border-radius: var(--ehs-radius-lg);
  box-shadow: var(--ehs-shadow-card);
  padding: 20px 22px;
  margin-bottom: 14px;
}

.chart-grid .dashboard-card,
.lower-grid .dashboard-card {
  margin-bottom: 0;
}

.card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.card-header h3 {
  margin: 0;
  color: var(--ehs-color-ink);
  font-family: var(--ehs-font-display);
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: -0.015em;
}

.card-header p {
  margin: 4px 0 0;
  color: var(--ehs-color-muted);
  font-size: 0.78rem;
}

.card-legend {
  display: flex;
  gap: 14px;
  flex-shrink: 0;
}

.card-legend .lg {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.74rem;
  font-weight: 600;
  color: var(--ehs-color-muted);
}

.card-legend .dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  display: inline-block;
}

.view-all {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  font-size: 0.76rem;
  font-weight: 600;
  color: var(--ehs-color-primary-active);
  text-decoration: none;
}

.view-all .pi {
  font-size: 0.68rem;
}

.view-all:hover {
  color: var(--ehs-color-primary-hover);
}

/* Lower grid: recent + highlights */
.lower-grid {
  display: grid;
  grid-template-columns: 1.6fr 1fr;
  gap: 14px;
}

.recent-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.recent-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 0;
  border-top: 1px solid var(--ehs-color-border);
}

.recent-item:first-child {
  border-top: 0;
}

.recent-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 32px 12px;
  color: var(--ehs-color-muted);
  font-size: 0.82rem;
  text-align: center;
}

.recent-empty .pi {
  font-size: 1.4rem;
  color: var(--ehs-color-soft);
}

.recent-bank {
  flex-shrink: 0;
  min-width: 52px;
  padding: 4px 8px;
  border-radius: var(--ehs-radius-sm);
  background: var(--ehs-color-canvas);
  border: 1px solid var(--ehs-color-border);
  color: var(--ehs-color-text);
  font-size: 0.7rem;
  font-weight: 700;
  text-align: center;
  text-transform: uppercase;
}

.recent-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1 1 auto;
}

.recent-amount {
  font-size: 0.86rem;
  font-weight: 600;
  color: var(--ehs-color-ink);
}

.recent-ref {
  font-family: var(--ehs-font-mono);
  font-size: 0.72rem;
  color: var(--ehs-color-soft);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.recent-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
}

.recent-date {
  font-size: 0.7rem;
  color: var(--ehs-color-soft);
}

/* Highlights */
.highlight-rows {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.highlight-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 12px;
  border-radius: var(--ehs-radius-md);
  background: var(--ehs-color-canvas);
}

.hl-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  font-size: 0.85rem;
  flex-shrink: 0;
}

.hl-icon.green { background: #f0fdf4; color: var(--ehs-color-success); }
.hl-icon.teal { background: #f0fdfa; color: #0d9488; }
.hl-icon.indigo { background: var(--ehs-color-primary-soft); color: var(--ehs-color-primary); }
.hl-icon.blue { background: #ecfeff; color: #0ea5e9; }

.hl-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.hl-label {
  font-size: 0.74rem;
  color: var(--ehs-color-muted);
  font-weight: 500;
}

.hl-value {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--ehs-color-ink);
  text-transform: capitalize;
}

.hl-value small {
  font-weight: 500;
  color: var(--ehs-color-soft);
}

@media (max-width: 1200px) {
  .stats-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 991.98px) {
  .dashboard-hero h1 { font-size: 1.45rem; }
  .stats-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .chart-grid-2 { grid-template-columns: 1fr; }
  .lower-grid { grid-template-columns: 1fr; }

  .sub-banner {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .sub-banner-usage {
    flex-basis: auto;
    max-width: none;
  }

  .sub-banner-cta {
    justify-content: center;
  }
}

@media (max-width: 520px) {
  .stats-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
</style>
